/**
 * THE ENTITY PANEL'S MODEL (plan 5; design docs/superpowers/specs/2026-10-04-entity-panel-design.md; spec Q50–Q51).
 * Pure: everything the right panel shows, rebuilt from the page's state on every change — nothing is copied, so
 * nothing goes stale (N18). Every rule is asked of the rules util (ADR 0008/0009); the panel decides nothing.
 * ⚠ `import type` only for models.
 */
import type { Entity, TypeData } from '../../../../model/entity.model';
import type { RoleInfo } from '../zs-entity.types';
import {
  canEditAllFieldSettings, canHaveSampleRows, canOpenFieldForEdit, isFinalized, isFullAccessRole, isSystemField,
  lockedFieldEditableSettings, sampleRowFields,
} from '../rules/entity-form-rules';
import { typeLabel } from '../concepts/type-vocabulary';

export type Tone = 'amber' | 'sky' | 'emerald' | 'rose';
export interface StatusChip { label: string; tone: Tone; detail: string }
export type GroupKey = 'finalized' | 'new' | 'local';

export interface OverviewRow { key: string; name: string; chip: StatusChip; fieldCount: number; linksTo: string[]; isOpen: boolean }
export interface OverviewGroup { key: GroupKey; title: string; rows: OverviewRow[] }
export interface Overview {
  open: { key: string; name: string; chip: StatusChip; unsaved: number } | null;
  groups: OverviewGroup[];
  /** Visible entities before search — 0 = an empty project. */
  total: number;
}

export interface FieldNode {
  path: string[]; name: string; type: string; badge: string; typeLabel: string;
  link: string | null; defaultText: string | null; description: string | null;
  locked: boolean; canChange: string; canEdit: boolean; children: FieldNode[];
}
export interface AccessRow { role: string; builtIn: boolean; crud: { create: boolean; read: boolean; update: boolean; delete: boolean } | null }
export interface EntityLink { key: string; name: string }
export interface EntityDetail {
  key: string; name: string; chip: StatusChip; canChange: string; isOpen: boolean; hidden: boolean; unsaved: number;
  fields: FieldNode[]; systemFields: string[]; linksTo: EntityLink[]; linkedFrom: EntityLink[];
  access: AccessRow[] | null;
  sampleRows: { columns: string[]; rows: string[][]; total: number } | null;
}

/** What the panel reads from the page. */
export interface PanelState { entities: readonly Entity[]; open: Entity | null; unsaved: number; roles: readonly RoleInfo[] }

/** The key of a new entity that is open but was never saved (it has no id yet). */
export const OPEN_NEW_KEY = '__open_new__';
export const entityKey = (e: Entity): string => e.id || OPEN_NEW_KEY;

// Was (2026-10-04, plan 5 T3 polish, A1 — ID and JSON fields showed a bare "·"):
// const BADGES: Record<string, string> = {
//   STRING: 'Aa', NUMBER: '#', BOOLEAN: '✓', TIMESTAMP: '◷', ENTITY_REFERENCE: '↗', ARRAY_OF_OBJECT: '☰', ATTACHMENT: '⎙', GEOMETRY: '⌖',
// };
const BADGES: Record<string, string> = {
  STRING: 'Aa', NUMBER: '#', BOOLEAN: '✓', TIMESTAMP: '◷', ENTITY_REFERENCE: '↗', ARRAY_OF_OBJECT: '☰', ATTACHMENT: '⎙', GEOMETRY: '⌖',
  UUID: 'ID', AUTO_INCREMENT: '#', JSONB: '{}',
};
export const typeBadge = (type: string): string => BADGES[type] ?? '·';

export function statusChip(e: { id?: string; status?: string }): StatusChip {
  switch (e.status) {
    case 'LOCAL': return { label: 'Local', tone: 'sky', detail: 'In the app only' };
    case 'NEW': return { label: 'New', tone: 'amber', detail: e.id ? 'Saved, not finalized' : 'Not saved yet' };
    case 'ERROR': return { label: 'Finalized', tone: 'rose', detail: 'Error building the table' };
    case 'PENDING': return { label: 'Finalized', tone: 'emerald', detail: 'Table being built' };
    case 'READY': return { label: 'Finalized', tone: 'emerald', detail: 'Ready' };
    case 'SYNCED': return { label: 'Finalized', tone: 'emerald', detail: 'Synced' };
    default: return { label: e.status || 'New', tone: 'amber', detail: '' };
  }
}

// Was (2026-10-04, plan 5 final review, I4 — not true for Pending/Error, only Ready/Synced; plan 6 T1 — a LOCAL entity's
// banner said "It can carry sample rows" even when it couldn't, and never said names/types are fixed once saved):
// export function canChangeText(e: { id?: string; status?: string }): string {
//   if (e.status === 'LOCAL') return 'In the app only — no table. It can carry sample rows.';
//   if (isFinalized(e.status)) return 'The table exists — only defaults and descriptions can change.';
//   return 'Everything can still change — nothing is built yet.';
// }
export function canChangeText(e: { id?: string; status?: string; data?: TypeData[] }): string {
  if (e.status === 'LOCAL') {
    // Was (2026-10-04, plan 6 final review, M3 — asked `e.id` directly instead of the rule):
    // const what = e.id ? 'Names and types are fixed; defaults and descriptions can change.' : 'Everything can still change until it is saved.';
    // Plan 6 — a saved local entity's names and types are fixed (canEditAllFieldSettings); rows only where the page offers them.
    const what = canEditAllFieldSettings(e) ? 'Everything can still change until it is saved.' : 'Names and types are fixed; defaults and descriptions can change.';
    const rows = canHaveSampleRows({ status: e.status, data: e.data ?? [] }) ? ' It can carry sample rows.' : '';
    return `In the app only — no table. ${what}${rows}`;
  }
  if (isFinalized(e.status)) return 'Finalized — names and types are fixed; only defaults and descriptions can change.';
  return 'Everything can still change — nothing is built yet.';
}

/** A hidden entity (the platform's own) reached through a link — shown for reference; the page never opens it. */
export const HIDDEN_TEXT = 'Managed by Zavo — shown for reference; it can’t be opened or changed here.';

const GROUP_TITLES: Record<GroupKey, string> = { finalized: 'Finalized', new: 'New', local: 'Local' };
const groupOf = (e: Entity): GroupKey => (e.status === 'LOCAL' ? 'local' : isFinalized(e.status) ? 'finalized' : 'new');
const SETTING_WORDS: Record<string, string> = { defaultValue: 'default', defaultMode: 'default mode', description: 'description' };
/** The order the settings are named in — the rules util's list order is not a reading order. */
const SETTING_ORDER = ['defaultValue', 'defaultMode', 'description'];
const andList = (xs: string[]): string => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`);
// Was (2026-10-04, plan 6 T1 — a link to a deleted entity names it only inside this file):
// const GONE = 'an entity that no longer exists';
export const MISSING_ENTITY = 'an entity that no longer exists';
const GONE = MISSING_ENTITY;

/** What the builder sees: hidden entities left out, the open entity's unsaved edits laid over its saved copy. */
function visibleWithOpen(entities: readonly Entity[], open: Entity | null): Entity[] {
  const base = entities.filter((e) => !e.hidden);
  if (!open) return base;
  if (!open.id) return [...base, open];
  return base.map((e) => (e.id === open.id ? open : e));
}

/** Every entity's name by id — hidden ones included, so a link to a hidden entity names it (T1 review I1). */
function namesById(entities: readonly Entity[], open: Entity | null): Map<string, string> {
  const m = new Map(entities.map((e) => [e.id, e.name]));
  if (open?.id) m.set(open.id, open.name);
  return m;
}

function walk(data: readonly TypeData[] | undefined, fn: (f: TypeData) => void): void {
  for (const f of data ?? []) {
    fn(f);
    if (f.type === 'ARRAY_OF_OBJECT') walk(f.data, fn);
  }
}

/** The ids an entity links to (its own link fields, nested ones too; never the system parent_id). */
function linkTargets(e: Entity): string[] {
  const ids: string[] = [];
  walk(e.data, (f) => { if (f.type === 'ENTITY_REFERENCE' && !isSystemField(f.name) && f.referenceEntityName) ids.push(f.referenceEntityName); });
  return [...new Set(ids)];
}

/** A default worth showing — the type's blank default (empty text, 0, a false yes/no, a date with mode none) is not shown. */
function defaultText(f: TypeData): string | null {
  const mode = (f as { defaultMode?: string }).defaultMode;
  if (f.type === 'TIMESTAMP') {
    // Was (2026-10-04, plan 5 final review, I5 — didn't follow field-default.util.ts's rule):
    // if (mode === 'now') return 'Now';
    // return mode === 'fixed' && f.defaultValue ? String(f.defaultValue) : null;
    if (mode === 'none') return null;
    if (mode === 'fixed' && f.defaultValue) return String(f.defaultValue);
    return 'Now'; // 'now' or no mode — the page's rule: an absent mode means now (field-default.util.ts)
  }
  const v = f.defaultValue as unknown;
  if (v === null || v === undefined || v === '') return null;
  if (f.type === 'NUMBER' && v === 0) return null;
  if (f.type === 'BOOLEAN') return v === true || v === 'true' ? 'Yes' : null;
  if (['ENTITY_REFERENCE', 'ARRAY_OF_OBJECT', 'GEOMETRY', 'ATTACHMENT'].includes(f.type)) return null;
  // Was (2026-10-04, plan 5 final review, minor — an object default rendered as "[object Object]"):
  // return String(v);
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

function fieldNodes(data: readonly TypeData[] | undefined, path: string[], entity: Entity, nameOf: Map<string, string>, isOpen: boolean): FieldNode[] {
  const all = canEditAllFieldSettings(entity);
  return (data ?? []).filter((f) => !isSystemField(f.name)).map((f): FieldNode => ({
    path,
    name: f.name,
    type: f.type,
    badge: typeBadge(f.type),
    typeLabel: typeLabel(f.type),
    link: f.type === 'ENTITY_REFERENCE' ? (nameOf.get(f.referenceEntityName) ?? (f.referenceEntityName ? GONE : null)) : null,
    defaultText: defaultText(f),
    description: f.description?.trim() || null,
    locked: !all,
    canChange: all ? 'Everything' : `Only its ${andList(SETTING_ORDER.filter((k) => lockedFieldEditableSettings(f).includes(k)).map((k) => SETTING_WORDS[k]))}`,
    canEdit: isOpen && canOpenFieldForEdit(f),
    children: f.type === 'ARRAY_OF_OBJECT' ? fieldNodes(f.data, [...path, f.name], entity, nameOf, isOpen) : [],
  }));
}

export function buildOverview(state: PanelState, search = ''): Overview {
  const all = visibleWithOpen(state.entities, state.open);
  // Was (2026-10-04, plan 5 T1 review, I1): const nameOf = new Map(all.map((e) => [e.id, e.name]));
  const nameOf = namesById(state.entities, state.open);
  const q = search.trim().toLowerCase();
  const matches = (e: Entity): boolean => {
    if (!q || (e.name || '').toLowerCase().includes(q)) return true;
    let hit = false;
    walk(e.data, (f) => { if (!isSystemField(f.name) && f.name.toLowerCase().includes(q)) hit = true; });
    return hit;
  };
  const openKey = state.open ? entityKey(state.open) : null;
  const items = all.filter(matches).map((e) => ({
    group: groupOf(e),
    row: {
      key: entityKey(e),
      name: e.name || 'New entity',
      chip: statusChip(e),
      // Was (2026-10-04, plan 6 T1 — the page's card counts every field; the panel left out id and the two timestamps):
      // fieldCount: (e.data ?? []).filter((f) => !isSystemField(f.name)).length,
      fieldCount: (e.data ?? []).length, // the page's count: every field, Zavo's included
      linksTo: linkTargets(e).map((id) => nameOf.get(id) ?? GONE),
      isOpen: entityKey(e) === openKey,
    } as OverviewRow,
  }));
  const groups = (['finalized', 'new', 'local'] as GroupKey[])
    .map((key) => ({
      key,
      title: GROUP_TITLES[key],
      rows: items.filter((i) => i.group === key).map((i) => i.row).sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .filter((g) => g.rows.length);
  return {
    open: state.open ? { key: openKey as string, name: state.open.name || 'New entity', chip: statusChip(state.open), unsaved: state.unsaved } : null,
    groups,
    total: all.length,
  };
}

const cell = (v: unknown): string =>
  v === null || v === undefined ? '' : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : typeof v === 'object' ? JSON.stringify(v) : String(v);

export function buildDetail(key: string, state: PanelState): EntityDetail | null {
  const all = visibleWithOpen(state.entities, state.open);
  // Was (2026-10-04, plan 5 T3 polish, A2 — review M2): const e = all.find((x) => entityKey(x) === key);
  // A hidden entity is not listed, but a link may name it — the inspector still shows it, read-only (T3 polish A2).
  const e = all.find((x) => entityKey(x) === key) ?? state.entities.find((x) => x.id === key);
  if (!e) return null;
  // Was (2026-10-04, plan 5 T1 review, I1): const nameOf = new Map(all.map((x) => [x.id, x.name]));
  const nameOf = namesById(state.entities, state.open);
  const isOpen = !!state.open && entityKey(state.open) === key;
  const hidden = !!e.hidden;
  const linksTo = linkTargets(e).map((id) => ({ key: id, name: nameOf.get(id) ?? GONE }));
  const linkedFrom = e.id
    ? all.filter((x) => x !== e && linkTargets(x).includes(e.id)).map((x) => ({ key: entityKey(x), name: x.name || 'New entity' }))
    : [];
  const admin = state.roles.find((r) => isFullAccessRole(r));
  // Was (2026-10-04, plan 5 final review, C1 — a hidden entity's access is not the builder's to see):
  // const access: AccessRow[] | null = e.id
  const access: AccessRow[] | null = e.id && !hidden
    ? [
        { role: admin?.name ?? 'Admin', builtIn: true, crud: null },
        ...state.roles
          .filter((r) => !isFullAccessRole(r) && r.permissions?.[e.id])
          .map((r) => ({ role: r.name, builtIn: false, crud: { ...r.permissions[e.id] } })),
      ]
    : null;
  let sampleRows: EntityDetail['sampleRows'] = null;
  // Was (2026-10-04, plan 5 T1 review, I2): if (e.status === 'LOCAL') {
  if (canHaveSampleRows(e)) {
    const columns = sampleRowFields(e.data).map((f) => f.name);
    const rows = (e.predefinedData ?? []) as Record<string, unknown>[];
    sampleRows = { columns, rows: rows.slice(0, 10).map((r) => columns.map((c) => cell(r[c]))), total: rows.length };
  }
  return {
    // Was (2026-10-04, plan 5 final review, C1 — a hidden entity is shown for reference only):
    // key, name: e.name || 'New entity', chip: statusChip(e), canChange: canChangeText(e), isOpen,
    key, name: e.name || 'New entity', chip: statusChip(e), canChange: hidden ? HIDDEN_TEXT : canChangeText(e), isOpen, hidden,
    unsaved: isOpen ? state.unsaved : 0, fields: fieldNodes(e.data, [], e, nameOf, isOpen),
    systemFields: (e.data ?? []).filter((f) => isSystemField(f.name)).map((f) => f.name),
    linksTo, linkedFrom, access, sampleRows,
  };
}
